"use client";

import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useWatchDogContext } from "@/context/WatchDogContext";
import {
  FormDataProfile,
  FormDataWatchDog,
  LoggedInItem,
  NewFeatureRequest,
  OtherItem,
  ProfileItem,
} from "@/types";
import { addNewWatchDog, featureProposal, setNewPassword } from "@/utils";
import {
  LOGGED_IN_ITEMS,
  OTHER_ITEMS,
  USER_ESTATE_ITEMS,
} from "@/utils/HelperConstants";
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

type MenuItemsContextType = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleDiscardChanges: () => void;
  saveWatchDog: (data?: FormDataWatchDog) => Promise<void>;
  openEditModal: () => void;
  stepNumber: number;
  setStepNumber: Dispatch<SetStateAction<number>>;
  resetPassword: () => Promise<void>;
  saveNewFeatureRequest: () => Promise<void>;
  formDataWatchDog: FormDataWatchDog;
  setFormDataWatchDog: Dispatch<SetStateAction<FormDataWatchDog>>;
  resetDetailSearch: () => void;
  selectedItem: any | null;
  setSelectedItem: Dispatch<SetStateAction<any | null>>;
  LOGGED_IN_ITEMS: any[];
  USER_ESTATE_ITEMS: any[];
  newFeatureDetails: NewFeatureRequest;
  setNewFeatureDetails: Dispatch<SetStateAction<NewFeatureRequest>>;
};

const defaultContext: MenuItemsContextType = {
  isOpen: false,
  setIsOpen: () => {},
  handleDiscardChanges: () => {},
  saveWatchDog: async () => {},
  openEditModal: () => {},
  stepNumber: 0,
  setStepNumber: () => {},
  resetPassword: async () => {},
  saveNewFeatureRequest: async () => {},
  formDataWatchDog: {} as FormDataWatchDog,
  setFormDataWatchDog: () => {},
  resetDetailSearch: () => {},
  selectedItem: null,
  setSelectedItem: () => {},
  LOGGED_IN_ITEMS: [],
  USER_ESTATE_ITEMS: [],
  newFeatureDetails: {} as NewFeatureRequest,
  setNewFeatureDetails: () => {},
};

const MenuItemsContext = createContext<MenuItemsContextType>(defaultContext);

const getDefaultWatchDogForm = (membership?: string): FormDataWatchDog => ({
  name: "",
  id: 0,
  client: null,
  isNew: true,
  doResetSearch: false,
  isFirstSyncCompleted: false,
  isEnabled: true,
  realityUrl: ["Sreality", "Bezrealitky", "Bazos", "Idnes", "Realitymat"],
  estateType: "",
  offerType: "",
  flatCategory: [],
  homeCategory: [],
  landCategory: [],
  comercialCategory: [],
  otherCategory: [],
  exactAddress: "",
  searchAddress: "",
  inRange: "Nezadáno",
  estateCondition: [],
  floorFrom: null,
  floorTo: null,
  usableAreaFrom: null,
  usableAreaTo: null,
  priceFrom: null,
  priceTo: null,
  urlSreality: "",
  urlBazos: "",
  urlBezrealitky: "",
  urlIdnesReality: "",
  doEmailReport: true,
  emailReportFrequency:
    membership === "STANDARD"
      ? "DAILY"
      : membership === "PREMIUM" || membership === "BASIC"
        ? "OFTEN"
        : "NONSTOP",
  emailReportSetting:
    membership === "STANDARD"
      ? "GROUP_BY_ALL"
      : membership === "PREMIUM" || membership === "BASIC"
        ? "GROUP_BY_WATCHDOG"
        : "SINGLE",
  sendOfferAs: "NEW",
});

export const MenuItemsProvider = ({ children }: { children: ReactNode }) => {
  const { userInfoGlobal, setErrorMessage } = useAppContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      passwordsDoNotMatch: "Hesla se neshodují!",
    },
    en: {
      passwordsDoNotMatch: "Passwords do not match!",
    },
  }[language];

  const { getWatchDogs, selectedDog } = useWatchDogContext();
  const { formData, setFormData } = useAppContext();

  const [isOpen, setIsOpen] = useState(false);
  const [stepNumber, setStepNumber] = React.useState(1);
  const [, setPasswordMatch] = useState(true);
  const [selectedItem, setSelectedItem] = useState<
    ProfileItem | LoggedInItem | OtherItem | null
  >(null);
  const [formDataWatchDog, setFormDataWatchDog] = useState<FormDataWatchDog>(
    getDefaultWatchDogForm(userInfoGlobal?.membership),
  );

  const [, setIsLoading] = useState(false);
  const [newFeatureDetails, setNewFeatureDetails] = useState(
    defaultContext.newFeatureDetails as NewFeatureRequest,
  );

  const openEditModal = () => {
    setIsOpen(true);
    setSelectedItem(OTHER_ITEMS[0]);
    setFormDataWatchDog({
      name: "",
      id: selectedDog?.id ?? 0,
      client: selectedDog?.client ?? null,
      isNew: false,
      doResetSearch: false,
      isFirstSyncCompleted: selectedDog?.isFirstSyncCompleted ?? false,
      isEnabled: selectedDog?.isEnabled ?? true,
      realityUrl: selectedDog?.realityUrl ?? [],
      estateType: selectedDog?.estateType ?? "",
      offerType: selectedDog?.offerType ?? "",
      flatCategory: selectedDog?.flatCategory ?? [],
      homeCategory: selectedDog?.homeCategory ?? [],
      landCategory: selectedDog?.landCategory ?? [],
      comercialCategory: selectedDog?.comercialCategory ?? [],
      otherCategory: selectedDog?.otherCategory ?? [],
      exactAddress: selectedDog?.exactAddress ?? "",
      searchAddress: selectedDog?.searchAddress ?? "",
      inRange: selectedDog?.inRange ?? "Nezadáno",
      estateCondition: selectedDog?.estateCondition ?? [],
      floorFrom: selectedDog?.floorFrom ?? null,
      floorTo: selectedDog?.floorTo ?? null,
      usableAreaFrom: selectedDog?.usableAreaFrom ?? null,
      usableAreaTo: selectedDog?.usableAreaTo ?? null,
      priceFrom: selectedDog?.priceFrom ?? null,
      priceTo: selectedDog?.priceTo ?? null,
      urlSreality: selectedDog?.urlSreality ?? "",
      urlBazos: selectedDog?.urlBazos ?? "",
      urlBezrealitky: selectedDog?.urlBezrealitky ?? "",
      urlIdnesReality: selectedDog?.urlIdnesReality ?? "",
      doEmailReport: selectedDog?.doEmailReport ?? true,
      emailReportFrequency:
        selectedDog?.emailReportFrequency ??
        (userInfoGlobal?.membership === "STANDARD"
          ? "DAILY"
          : userInfoGlobal?.membership === "PREMIUM" ||
              userInfoGlobal?.membership === "BASIC"
            ? "OFTEN"
            : "NONSTOP"),
      emailReportSetting:
        selectedDog?.emailReportSetting ??
        (userInfoGlobal?.membership === "STANDARD"
          ? "GROUP_BY_ALL"
          : userInfoGlobal?.membership === "PREMIUM" ||
              userInfoGlobal?.membership === "BASIC"
            ? "GROUP_BY_WATCHDOG"
            : "SINGLE"),
      sendOfferAs: selectedDog?.sendOfferAs ?? "NEW",
    });
  };

  const handleDiscardChanges = () => {
    setIsOpen(false);
    if (userInfoGlobal) {
      setFormData({
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
        isActive: userInfoGlobal?.isActive ?? true,
        userName: userInfoGlobal.userName ?? null,
        subject: userInfoGlobal.subject,
        idNumber: userInfoGlobal.idNumber,
        address: userInfoGlobal.address,
        oldPassword: null,
        newPassword: null,
        passwordRepeat: null,
        membership: userInfoGlobal.membership,
      });
      setFormDataWatchDog(getDefaultWatchDogForm(userInfoGlobal.membership));
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setStepNumber(1);
    }
  }, [isOpen]);

  useEffect(() => {
    handleDiscardChanges();
  }, [userInfoGlobal]);

  useEffect(() => {
    if (selectedItem?.name === "Vytvořit nového") {
      setFormDataWatchDog((prev) =>
        prev.isNew &&
        prev.id === 0 &&
        prev.name === "" &&
        prev.estateType === ""
          ? getDefaultWatchDogForm(userInfoGlobal?.membership)
          : prev,
      );
    }
  }, [selectedItem, userInfoGlobal]);

  async function saveWatchDog(data?: FormDataWatchDog) {
    setIsOpen(false);
    setStepNumber(1);
    await saveWatchDogInfo(data ?? formDataWatchDog);
    await getWatchDogs(false);
  }

  const saveWatchDogInfo = async (formDataWatchDog: FormDataWatchDog) => {
    if (!userInfoGlobal?.token) return;

    try {
      const payload = {
        email: userInfoGlobal!.email,
        token: userInfoGlobal!.token,
        data: formDataWatchDog,
      };
      await addNewWatchDog(payload, userInfoGlobal!.token);
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      handleDiscardChanges();
    }
  };

  const handlePasswordReset = async (formData: FormDataProfile) => {
    setIsLoading(true);

    const payload: Record<string, any> = {
      email: userInfoGlobal!.email,
      token: userInfoGlobal!.token,
      isActive: formData.isActive,
      oldPassword: formData.oldPassword,
      newPassword: formData.newPassword,
      passwordRepeat: formData.passwordRepeat,
    };

    if (payload.newPassword !== payload.passwordRepeat) {
      setPasswordMatch(false);
      setErrorMessage(copy.passwordsDoNotMatch);
      setIsLoading(false);
      return;
    }
    setPasswordMatch(true);
    delete payload.passwordRepeat;
    try {
      const response = await setNewPassword(payload, payload.token);
      if (response.status === 200) {
        handleDiscardChanges();
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setIsLoading(false);
    }
  };

  async function saveNewFeatureRequest() {
    handleNewFeatureRequest(newFeatureDetails);
  }
  const handleNewFeatureRequest = async (
    newFeatureDetails: NewFeatureRequest,
  ) => {
    setIsLoading(true);

    const payload: Record<string, any> = {
      email: userInfoGlobal!.email,
      token: userInfoGlobal!.token,
      data: newFeatureDetails,
    };

    try {
      const response = await featureProposal(payload, payload.token);
      if (response.status === 200) {
        handleDiscardChanges();
      }
    } catch (err) {
      setErrorMessage(String(err));
    } finally {
      setNewFeatureDetails(
        defaultContext.newFeatureDetails as NewFeatureRequest,
      );
      setIsLoading(false);
    }
  };

  async function resetPassword() {
    handlePasswordReset(formData);
  }

  function resetDetailSearch() {
    setStepNumber(1);
    setFormDataWatchDog({
      ...formDataWatchDog,
      doResetSearch: false,
      flatCategory: [],
      homeCategory: [],
      landCategory: [],
      comercialCategory: [],
      otherCategory: [],
      estateCondition: [],
      floorFrom: null,
      floorTo: null,
      priceFrom: null,
      priceTo: null,
      usableAreaFrom: null,
      usableAreaTo: null,
    });
  }

  return (
    <MenuItemsContext.Provider
      value={{
        isOpen,
        setIsOpen,
        handleDiscardChanges,
        saveWatchDog,
        openEditModal,
        setStepNumber,
        resetPassword,
        stepNumber,
        resetDetailSearch,
        formDataWatchDog,
        setFormDataWatchDog,
        selectedItem,
        setSelectedItem,
        LOGGED_IN_ITEMS,
        USER_ESTATE_ITEMS,
        newFeatureDetails,
        setNewFeatureDetails,
        saveNewFeatureRequest,
      }}
    >
      {children}
    </MenuItemsContext.Provider>
  );
};

export const useMenuItemsContext = () => useContext(MenuItemsContext);
